import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BrandProvider } from '@cover/core/brand.tsx';
import { ApiError } from '@cover/core/api/client.ts';
import { Spinner, ErrorState } from '@cover/core/ui/States.tsx';
import { ToastProvider } from '@cover/core/ui/Toast.tsx';
import { ErrorBoundary } from '@cover/core/ui/ErrorBoundary.tsx';
import { SessionProvider, useMe, useSessionConfig, useSignOutOn401 } from './session.tsx';
import { DemoNotice } from '@cover/core/ui/DemoNotice.tsx';
import { Layout } from './Layout.tsx';
import { HomePage } from './pages/Home.tsx';
import { BusinessPage } from './pages/Business.tsx';
import { PoliciesPage } from './pages/Policies.tsx';
import { QuotesPage } from './pages/Quotes.tsx';
import { EquipmentPage } from './pages/Equipment.tsx';
import { ClaimsPage } from './pages/Claims.tsx';
import { ClaimPage } from './pages/Claim.tsx';
import { NewClaimPage } from './pages/NewClaim.tsx';
import { DocumentsPage } from './pages/Documents.tsx';
import { ConnectionsPage } from './pages/Connections.tsx';
import { ReferPage } from './pages/Refer.tsx';
import { LoginPage } from './pages/Login.tsx';
import { AuthPage } from './pages/Auth.tsx';
import { PaymentPage } from './pages/Payment.tsx';
import { WelcomePage } from './pages/Welcome.tsx';
import { ChatProvider } from './components/Chat.tsx';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2 },
  },
});

function SignedIn() {
  useSignOutOn401();
  const me = useMe();
  const location = useLocation();
  if (me.isLoading) return <Spinner />;
  if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (me.error || !me.data) return <ErrorState error={me.error} retry={() => me.refetch()} />;
  if (me.data.kind !== 'customer') return <ErrorState error={new ApiError(403, 'not_customer', 'This dashboard is for customers.')} />;
  // A new sign-up has no business until they tell us about it: a welcome page and their card, nothing else.
  if (!me.data.businessIds?.length)
    return (
      <SessionProvider me={me.data}>
        <ChatProvider>
        <Layout>
          <Routes>
            <Route index element={<WelcomePage />} />
            <Route path="account/payment" element={<PaymentPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
        </ChatProvider>
      </SessionProvider>
    );
  return (
    <SessionProvider me={me.data}>
      <ChatProvider>
      <Layout>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="business" element={<BusinessPage />} />
          <Route path="quotes" element={<QuotesPage />} />
          <Route path="quotes/:quoteId" element={<QuotesPage />} />
          <Route path="account/payment" element={<PaymentPage />} />
          {/* Older /me/next links. */}
          <Route path="tools" element={<Navigate to="/equipment" replace />} />
          <Route path="policies/:policyNumber" element={<Navigate to="/policies" replace />} />
          <Route path="business/:businessId" element={<Navigate to="/business" replace />} />
          <Route path="policies" element={<PoliciesPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="equipment" element={<EquipmentPage />} />
          <Route path="claims" element={<ClaimsPage />} />
          <Route path="claims/new" element={<NewClaimPage />} />
          <Route path="claims/:claimId" element={<ClaimPage />} />
          <Route path="connections" element={<ConnectionsPage />} />
          <Route path="refer" element={<ReferPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
      </ChatProvider>
    </SessionProvider>
  );
}

function DemoBanner() {
  const config = useSessionConfig();
  // Shown until the config says otherwise, so it's never missing while loading.
  return config.data?.demoNotice === false ? null : <DemoNotice />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <DemoBanner />
      <BrandProvider fallback={<Spinner />}>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/*" element={<SignedIn />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </BrandProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
